import { TimeFormatter } from './field-time';
import util from './util';
/*
 * The TimeUnitFormatter class can be used when splitting a time field into separate
 * units such as hours and minutes. When the field has focus, a popover shows valid
 * time selection options to the user. When the value is changed, the original time
 * field (which is hidden on the page) will be updated with the new time.
 */
export default class TimeUnitFormatter {
    constructor(element) {
        this.el = element;
        this.combinedTimeEl = document.getElementById(element.dataset.combinedTimeElement);
        const [hh = '', mm = ''] = this.combinedTimeEl.value.split(':');

        const allow24hour = this.combinedTimeEl.dataset.timeAllowgt24hrs === 'true';
        const timeType = this.el.dataset?.timeType || 'hour';

        if (timeType === 'hour') {
            this.el.value = hh;
            this.maxValue = allow24hour ? 24 : 23;
            this.hourEl = this.el;
            this.minEl = document.querySelector(`#${this.combinedTimeEl.id}_mm`);
        } else {
            this.el.value = mm;
            this.maxValue = 59;
            this.hourEl = document.querySelector(`#${this.combinedTimeEl.id}_hh`);
            this.minEl = this.el;
        }

        // build the picker
        const { timeInterval } = this.combinedTimeEl.dataset;
        const pickerData = this.getPickerData(timeType, timeInterval, allow24hour);
        this.pickerEl = this.buildPicker(pickerData);
        this.el.after(this.pickerEl);
        this.pickerActive = false;

        this.el.addEventListener('click', (e) => this.handleOnClick(e));
        this.el.addEventListener('focus', (e) => this.handleOnFocus(e));
        this.el.addEventListener('blur', (e) => this.handleOnBlur(e));
        this.el.addEventListener('keypress', (e) => this.handleOnKeypress(e));
        this.el.addEventListener('keyup', (e) => this.handleOnKeyup(e));
    }

    getPickerData(timeType, interval, allow24hour) {
        if (timeType === 'hour') {
            const values = [
                ['00', '00 - 12am'],
                ['12', '12 - 12pm'],
                ['01', '01 - 1am'],
                ['13', '13 - 1pm'],
                ['02', '02 - 2am'],
                ['14', '14 - 2pm'],
                ['03', '03 - 3am'],
                ['15', '15 - 3pm'],
                ['04', '04 - 4am'],
                ['16', '16 - 4pm'],
                ['05', '05 - 5am'],
                ['17', '17 - 5pm'],
                ['06', '06 - 6am'],
                ['18', '18 - 6pm'],
                ['07', '07 - 7am'],
                ['19', '19 - 7pm'],
                ['08', '08 - 8am'],
                ['20', '20 - 8pm'],
                ['09', '09 - 9am'],
                ['21', '21 - 9pm'],
                ['10', '10 - 10am'],
                ['22', '22 - 10pm'],
                ['11', '11 - 11am'],
                ['23', '23 - 11pm'],
            ];

            // timekeeper allows users to set hours to '24:00' but some
            // other places with time fields do not.
            if (allow24hour) values.push(['24', '24 - 12am']);

            return {
                columns: 2,
                values,
                allow24hour,
            };
        }

        const intervalAsInt = parseInt(interval, 10) || 1;
        const values = [['00', '00']];

        for (let i = 1; i <= this.maxValue; i += 1) {
            if (i % intervalAsInt === 0) {
                const formattedValue = i < 10 ? `0${i}` : i;
                values.push([formattedValue, formattedValue]);
            }
        }

        // if the picker displays all seconds (0-59) then display
        // this as a 5 columns instead of just 1 column
        return {
            columns: intervalAsInt === 1 ? 5 : 1,
            values,
        };
    }

    buildPicker(pickerData) {
        const pickerContainer = document.createElement('div');
        pickerContainer.classList.add('time-unit-picker-container', 'd-none');
        pickerContainer.classList.toggle('allow24hour', !!pickerData.allow24hour);

        if (pickerData.columns === 2) {
            pickerContainer.classList.add('picker-col-2');
        } else if (pickerData.columns === 5) {
            pickerContainer.classList.add('picker-col-5');
        }

        // Align the picker directly under the field which has focus
        const { left } = this.el.getBoundingClientRect();
        pickerContainer.style.left = `${left}px`;

        pickerData.values.forEach(([value, description]) => {
            const item = document.createElement('span');
            item.classList.add('picker-item');
            item.textContent = description;
            item.dataset.value = value;

            pickerContainer.appendChild(item);
        });

        return pickerContainer;
    }

    // combine hours and mins together and assign to hidden field
    updateCombinedTimeElement() {
        const hh = this.hourEl.value;
        const mm = this.minEl.value;

        this.combinedTimeEl.value =
            hh === '' && mm === '' ? '' : TimeFormatter.reformat(`${hh}:${mm}`);
    }

    handleOnClickAway = (e) => {
        // ensure that clicking on an item in the popup does not make us
        // lose focus of the current element
        if (e.target === this.pickerActive || e.target === this.el) {
            return;
        }

        if (this.pickerEl.contains(e.target) && e.target.dataset.value) {
            e.preventDefault();
            this.el.value = e.target.dataset.value;
            this.updateCombinedTimeElement();

            // fire the onchange event so that mandatory field styling
            // is removed when a value is selected
            const event = new Event('change');
            this.el.dispatchEvent(event);
        }

        document.removeEventListener('mousedown', this.handleOnClickAway);
        util.showElement(this.pickerEl, false);
        this.pickerActive = false;
    };

    handleOnBlur(e) {
        if (this.pickerActive) {
            // Remove the picker when leaving the field
            document.removeEventListener('mousedown', this.handleOnClickAway);
            util.showElement(this.pickerEl, false);
            this.pickerActive = false;
        }

        const { value } = e.target;
        if (!value || value === this._valueOnFocus) return;

        const parsedValue = parseInt(value, 10);
        if (parsedValue < 10) e.target.value = `0${parsedValue}`;
        this._valueOnFocus = null;
    }

    handleOnClick(e) {
        if (!document.activeElement === e.target) {
            return;
        }

        if (!this.pickerActive) {
            util.showElement(this.pickerEl, true);
            this.pickerActive = true;
            document.addEventListener('mousedown', this.handleOnClickAway);
        }
    }

    handleOnFocus(e) {
        this._valueOnFocus = e.target.value;

        if (!this.pickerActive) {
            util.showElement(this.pickerEl, true);
            this.pickerActive = true;
            document.addEventListener('mousedown', this.handleOnClickAway);
        }
    }

    handleOnKeypress(e) {
        this._valueBeforeKeypress = e.target.value;

        const key = String.fromCharCode(e.charCode);

        if (!key || e.altKey || e.ctrlKey || e.shiftKey) return;

        // Ignore the pressed key if it's not a valid number
        if (!key.match(/^[0-9]$/)) e.preventDefault();
    }

    handleOnKeyup(e) {
        const { value } = e.target;

        if (!value) {
            if (value !== this._valueBeforeKeypress) {
                // value has been blanked out
                this.updateCombinedTimeElement();
            }
            return;
        }

        const formattedValue = parseInt(value, 10);

        if (Number.isNaN(formattedValue) || formattedValue > this.maxValue) {
            // the new value in this field is invalid so remove the last character
            // that the person tried to type
            if (value === this._valueBeforeKeypress) {
                // user held the same key down so take off the second digit
                e.target.value = e.target.value.charAt(0);
            } else {
                e.target.value = this._valueBeforeKeypress;
            }
        }

        this._valueBeforeKeypress = null;
        this.updateCombinedTimeElement();
    }
}

function init(addedNodes) {
    addedNodes.forEach((addedNode) => {
        addedNode.querySelectorAll('.form-input-time-unit').forEach((node) => {
            if (node._timeUnitFormatter) return;

            // ensure that the combined time field is present
            const combinedTimeField = node.dataset?.combinedTimeElement;
            if (!combinedTimeField || !document.getElementById(combinedTimeField)) {
                return;
            }

            // eslint-disable-next-line no-param-reassign
            node._timeUnitFormatter = new TimeUnitFormatter(node);
        });
    });
}

if (util) {
    util.initialiseAddedNodes(init);
}
